<template>
    <el-dialog title="绑定房间" :model-value="modelValue" top="10vh" width="640px" @close="closed" :close-on-click-modal="false">
        <div v-loading="loading" class="tree-wrapper">
            <el-tree
                ref="treeRef"
                :data="treeData"
                :props="treeProps"
                node-key="id"
                show-checkbox
                :default-expand-all="true"
                :default-expanded-keys="defaultExpandedKeys"
            >
                <template #default="{ node, data }">
                    <span :class="{ 'is-room': !!data.room_type }">
                        {{ data.name }}
                        <el-tag v-if="data.room_type" size="small" type="success" class="room-tag">房间</el-tag>
                    </span>
                </template>
            </el-tree>
        </div>
        <template #footer>
            <span class="dialog-footer">
                <el-button @click="closed">取消</el-button>
                <el-button type="primary" :loading="submitLoading" @click="onConfirm">确定</el-button>
            </span>
        </template>
    </el-dialog>
</template>

<script setup>
import { defineEmits, defineProps, ref, watch, nextTick } from 'vue'
import { ElMessage } from 'element-plus'

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    // 传入的树形数据
    treeData: {
        type: Array,
        default: () => []
    },
    // 已绑定的房间id数组
    boundRoomIds: {
        type: Array,
        default: () => []
    }
})

const emits = defineEmits(['update:modelValue', 'confirm'])

const treeRef = ref(null)
const loading = ref(false)
const submitLoading = ref(false)
const defaultExpandedKeys = ref([])

const treeProps = {
    label: 'name',
    children: 'children'
}

// 递归获取所有房间节点id
const getAllRoomIds = (data) => {
    const ids = []
    const traverse = (list) => {
        list.forEach((item) => {
            if (item.room_type) {
                ids.push(item.id)
            }
            if (item.children && item.children.length) {
                traverse(item.children)
            }
        })
    }
    traverse(data)
    return ids
}

// 递归查找父节点id路径
const findParentIds = (tree, targetId, parents = []) => {
    for (const node of tree) {
        if (node.id === targetId) {
            return parents
        }
        if (node.children && node.children.length) {
            const result = findParentIds(node.children, targetId, [...parents, node.id])
            if (result) return result
        }
    }
    return null
}

// 获取所有选中的房间节点（仅包含 room_type 的节点）
const getCheckedRoomIds = () => {
    const checkedNodes = treeRef.value.getCheckedNodes(false, false)
    // 过滤出只有 room_type 的房间节点
    return checkedNodes.filter((node) => !!node.room_type).map((node) => node.id)
}

// 确定
const onConfirm = async () => {
    const roomIds = getCheckedRoomIds()
    if (roomIds.length === 0) {
        ElMessage.warning('请至少选择一个房间')
        return
    }
    submitLoading.value = true
    try {
        emits('confirm', roomIds)
        submitLoading.value = false
        closed()
    } catch {
        submitLoading.value = false
    }
}

// 关闭
const closed = () => {
    emits('update:modelValue', false)
    nextTick(() => {
        treeRef.value?.setCheckedKeys([])
    })
}

// 监听打开，处理回显
watch(
    () => props.modelValue,
    (val) => {
        if (val && props.treeData.length) {
            nextTick(() => {
                // 只回显有 room_type 的房间节点
                const allRoomIds = getAllRoomIds(props.treeData)
                const validBoundIds = props.boundRoomIds.filter((id) => allRoomIds.includes(id))
                treeRef.value?.setCheckedKeys(validBoundIds, false)

                // 默认展开已选房间的父级路径
                const expanded = new Set()
                validBoundIds.forEach((id) => {
                    const parents = findParentIds(props.treeData, id)
                    if (parents) {
                        parents.forEach((pid) => expanded.add(pid))
                    }
                })
                defaultExpandedKeys.value = Array.from(expanded)
            })
        }
    }
)
</script>

<style scoped>
.tree-wrapper {
    max-height: 500px;
    overflow-y: auto;
    padding: 10px;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
}
.is-room {
    color: #409eff;
    font-weight: 500;
}
.room-tag {
    margin-left: 6px;
}
:deep(.el-tree-node__content) {
    height: 32px;
}
</style>
